import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Download, Package, Search, QrCode, Shield, Clock } from 'lucide-react';

const QRGenerator = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [secureQR, setSecureQR] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [generatingSecure, setGeneratingSecure] = useState(false);
  const qrRef = useRef();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/orders');
      setOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const createPackage = async (orderId) => {
    try {
      const response = await axios.post('/packages', {
        order_id: orderId,
        weight_kg: 1.0
      });
      toast.success('Paquete y QR generado exitosamente');
      
      // Update package in state
      setSelectedPackage(response.data);
      
      // Update order to mark it has a package
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { ...order, package_id: response.data.id } : order
      );
      setOrders(updatedOrders);
      
      return response.data;
    } catch (error) {
      toast.error('Error al crear paquete');
    }
  };

  const handleGenerateQR = async (order) => {
    setSelectedOrder(order);
    setSecureQR(null);
    
    // Check if order already has a package
    if (order.package_id) {
      toast.info('Este pedido ya tiene un paquete generado');
      // Fetch the existing package
      try {
        const response = await axios.get(`/packages/${order.package_id}`);
        setSelectedPackage(response.data);
      } catch (error) {
        // If package doesn't exist, create new one
        await createPackage(order.id);
      }
    } else {
      // Create new package
      await createPackage(order.id);
    }
  };

  const generateSecureQR = async () => {
    if (!selectedPackage) return;
    setGeneratingSecure(true);
    try {
      const res = await axios.post(`/packages/${selectedPackage.id}/generate-qr`);
      setSecureQR(res.data);
      toast.success('QR Seguro generado (válido 24 h)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando QR seguro');
    } finally {
      setGeneratingSecure(false);
    }
  };

  const downloadQR = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = 512;
    canvas.height = 512;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 512, 512);
      
      const link = document.createElement('a');
      link.download = `QR-${selectedPackage.qr_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('QR descargado exitosamente');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    return (
      order.id.toLowerCase().includes(searchLower) ||
      order.customer.name.toLowerCase().includes(searchLower) ||
      order.customer.phone.includes(searchTerm)
    );
  });

  return (
    <Layout user={user} onLogout={onLogout} title="Generador de QR">
      <div className="space-y-6" data-testid="qr-generator-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#224049' }}>Generador de Códigos QR</h1>
          <p className="text-sm" style={{ color: '#666' }}>Genera códigos QR para la cadena de custodia de pedidos</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left side - Order List */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle style={{ color: '#224049' }}>Seleccionar Pedido</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-4 h-4" style={{ color: '#666' }} />
                    <Input
                      placeholder="Buscar por ID, nombre o teléfono..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-order-input"
                    />
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {loading ? (
                      <p className="text-center py-4" style={{ color: '#666' }}>Cargando pedidos...</p>
                    ) : filteredOrders.length === 0 ? (
                      <p className="text-center py-4" style={{ color: '#666' }}>No hay pedidos</p>
                    ) : (
                      filteredOrders.map((order) => (
                        <Card
                          key={order.id}
                          className={`cursor-pointer hover:shadow-md transition-shadow ${
                            selectedOrder?.id === order.id ? 'ring-2' : ''
                          }`}
                          style={{
                            borderColor: selectedOrder?.id === order.id ? '#0F7A5B' : 'transparent'
                          }}
                          onClick={() => handleGenerateQR(order)}
                          data-testid={`order-item-${order.id}`}
                        >
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-semibold" style={{ color: '#224049' }}>
                                  {order.customer.name}
                                </p>
                                <p className="text-xs" style={{ color: '#666' }}>ID: {order.id.substring(0, 8)}</p>
                                <p className="text-xs" style={{ color: '#666' }}>{order.customer.phone}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <span className={order.mode === 'EXPRESS' ? 'mode-express' : 'mode-standard'}>
                                  {order.mode === 'EXPRESS' ? 'EXPRESS 60\'' : 'STANDARD'}
                                </span>
                                {order.package_id && (
                                  <div className="flex items-center gap-1 text-xs" style={{ color: '#0F7A5B' }}>
                                    <QrCode className="w-3 h-3" />
                                    <span>QR Generado</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right side - QR Display */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle style={{ color: '#224049' }}>Código QR Generado</CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedPackage ? (
                  <div className="flex flex-col items-center justify-center py-12" style={{ backgroundColor: '#EAE0D5', borderRadius: '8px' }}>
                    <Package className="w-16 h-16 mb-4" style={{ color: '#F0EBE3' }} />
                    <p style={{ color: '#666' }}>Selecciona un pedido para generar su QR</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* QR Code Display */}
                    <div className="flex justify-center p-6" style={{ backgroundColor: 'white', borderRadius: '8px' }} ref={qrRef}>
                      <QRCodeSVG
                        value={selectedPackage.qr_code}
                        size={256}
                        level="H"
                        includeMargin={true}
                        data-testid="qr-code-display"
                      />
                    </div>

                    {/* Package Info */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#EAE0D5' }}>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Código QR</p>
                          <p className="text-lg font-bold tracking-wider" style={{ color: '#224049' }}>
                            {selectedPackage.qr_code}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Código de Sello</p>
                          <p className="font-semibold" style={{ color: '#0F7A5B' }}>
                            {selectedPackage.seal_code}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Estado</p>
                          <span className="inline-block px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#D4AD6A', color: '#224049' }}>
                            {selectedPackage.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Order Info */}
                    {selectedOrder && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EBE3' }}>
                        <p className="text-xs font-medium mb-2" style={{ color: '#666' }}>Información del Pedido</p>
                        <div className="space-y-1">
                          <p className="text-sm" style={{ color: '#224049' }}>
                            <span className="font-semibold">Cliente:</span> {selectedOrder.customer.name}
                          </p>
                          <p className="text-sm" style={{ color: '#224049' }}>
                            <span className="font-semibold">Teléfono:</span> {selectedOrder.customer.phone}
                          </p>
                          <p className="text-sm" style={{ color: '#224049' }}>
                            <span className="font-semibold">PIN:</span> <span className="font-bold">{selectedOrder.pin}</span>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Secure QR (HMAC) section */}
                    <div
                      className="p-4 rounded-lg border-2"
                      style={{ backgroundColor: '#F3E8FF', borderColor: '#8E44AD' }}
                      data-testid="secure-qr-section"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4" style={{ color: '#8E44AD' }} />
                        <p className="text-sm font-bold" style={{ color: '#5B21B6' }}>
                          QR Seguro (HMAC + Expiración 24h)
                        </p>
                      </div>
                      {!secureQR ? (
                        <>
                          <p className="text-xs mb-3" style={{ color: '#5B21B6' }}>
                            Genera un QR de un solo uso firmado con HMAC-SHA256. Previene duplicados, expira en 24 horas
                            y solo puede escanearse una vez.
                          </p>
                          <Button
                            onClick={generateSecureQR}
                            disabled={generatingSecure}
                            className="w-full"
                            style={{ backgroundColor: '#8E44AD', color: 'white' }}
                            data-testid="generate-secure-qr-button"
                          >
                            <Shield className="w-4 h-4 mr-2" />
                            {generatingSecure ? 'Generando…' : 'Generar QR Seguro'}
                          </Button>
                        </>
                      ) : (
                        <div className="space-y-3">
                          <div
                            className="flex justify-center p-4 rounded"
                            style={{ backgroundColor: 'white' }}
                            data-testid="secure-qr-display"
                          >
                            <QRCodeSVG
                              value={JSON.stringify({
                                code: secureQR.code,
                                signature: secureQR.signature,
                                package_id: secureQR.package_id,
                              })}
                              size={220}
                              level="H"
                              includeMargin={true}
                            />
                          </div>
                          <div className="text-xs space-y-1" style={{ color: '#5B21B6' }}>
                            <p>
                              <span className="font-semibold">Código:</span>{' '}
                              <span className="font-mono">{secureQR.code}</span>
                            </p>
                            <p className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span className="font-semibold">Expira:</span>{' '}
                              {new Date(secureQR.expires_at).toLocaleString('es-MX')}
                            </p>
                            <p className="break-all">
                              <span className="font-semibold">Firma:</span>{' '}
                              <span className="font-mono">{secureQR.signature.slice(0, 32)}…</span>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Download Button */}
                    <Button
                      onClick={downloadQR}
                      className="w-full"
                      style={{ backgroundColor: '#0F7A5B', color: 'white' }}
                      data-testid="download-qr-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar QR como Imagen
                    </Button>

                    {/* Instructions */}
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: '#2E7D32' }}>📦 Instrucciones de Uso:</p>
                      <ul className="text-xs space-y-1" style={{ color: '#2E7D32' }}>
                        <li>• Imprime o muestra este QR al rider</li>
                        <li>• El rider escaneará el QR al recoger el paquete</li>
                        <li>• El cliente escaneará el QR al recibir el pedido</li>
                        <li>• Esto asegura la cadena de custodia completa</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default QRGenerator;